import { AttachInternalController } from "@nx-paeonia/utils-dom";
import { html, LitElement, type PropertyValues } from "lit";
import { property, queryAssignedElements } from "lit/decorators.js";
import { when } from "lit-html/directives/when.js";
import styles from "./radio-button.styles.js";

export class RadioButtonComponent extends LitElement {
	static formAssociated = true;
	private readonly _internals = new AttachInternalController(this);
	private radioElement: HTMLInputElement | undefined;

	@queryAssignedElements({ slot: "radio-element" })
	private _radioElements!: HTMLElement[];

	@property({ attribute: "checked", reflect: true, type: Boolean })
	_radioChecked = false;

	@property({ type: Boolean, attribute: "has-reset-button", reflect: true })
	hasResetButton = false;

	@property({ type: String, attribute: "matching-property" })
	matchingProperty = "";

	protected override firstUpdated(_changedProperties: PropertyValues): void {
		super.firstUpdated(_changedProperties);

		this.assignRadioElement();

		this.radioElement?.addEventListener("change", this.handleRadioChange.bind(this));
		this.addEventListener("click", this.handleSelfClick.bind(this));

		if (this.radioElement) {
			const matchingProperty = this.matchingProperty || "name";
			const matchingValue = this.radioElement.getAttribute(matchingProperty);
			document.querySelectorAll(`input[${matchingProperty}=${matchingValue}]`).forEach((radio) => {
				radio.addEventListener("change", this.handleRadioChange.bind(this));
			});
		}

		this.shadowRoot?.querySelector<HTMLButtonElement>('[type="reset"]')?.addEventListener("click", this.handleResetClick.bind(this));
	}

	protected override render() {
		return html`
      <button class="button-label">
        <slot name="radio-element"></slot>

        <slot></slot>

        ${when(this.hasResetButton, () => this.renderResetButton())}

        <footer class="button-footer">
          <slot name="title"></slot>
          <slot name="price"></slot>
        </footer>
      </button>
    `;
	}

	private renderResetButton() {
		return html`<button class="y-reset-button" type="reset">
      <slot name="reset-button-content"> &times; </slot>
    </button>`;
	}

	/**
	 * Handle internal changes following to slotted radio element changes.
	 */
	private handleRadioChange() {
		this._radioChecked = this.radioElement?.checked ?? false;
		this.requestUpdate();
	}

	private handleResetClick(event: MouseEvent) {
		event.stopPropagation();
		if (this.radioElement && this.radioElement.checked) {
			this.radioElement.checked = false;
			this.radioElement.dispatchEvent(new Event("change", { bubbles: true }));
		}
	}

	private handleSelfClick() {
		if (this.radioElement && !this.radioElement.checked) {
			this.radioElement.checked = true;
			this.radioElement.dispatchEvent(new Event("change", { bubbles: true }));
		}
	}

	private assignRadioElement() {
		if (this._radioElements.length !== 1) {
			console.warn(`Warning: <y-radio-button> required ONE slotted radio input element, but found ${this._radioElements.length} elements.`);
		}

		const radio = this._radioElements.find((el) => el instanceof HTMLInputElement && el.type === "radio") as HTMLInputElement | undefined;
		if (!radio) {
			console.warn('Warning: <y-radio-button> requires a slotted radio input element with slot="radio-element".');
		} else {
			this.radioElement = radio;
		}
	}

	static override get styles() {
		return styles;
	}
}

export function defineRadioButtonComponent() {
	if (!customElements.get("y-radio-button")) {
		customElements.define("y-radio-button", RadioButtonComponent);
	}
}
